import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Ref } from 'vue';

import { useSubscriptionStore } from '@/stores/index';

import router from '@/router';

import type { SubscriptionRouteName } from '@/types/router.type';
import { getIsPhysicalBatteryEnabled } from '@/utils/env.util';

import { ESubscriptionRouteName } from '@/constants/router.constants';

export default defineStore('step', () => {
  const allSteps = ref([
    {
      id: ESubscriptionRouteName.PDL,
      routeName: ESubscriptionRouteName.PDL,
      hasStepper: false,
    },
    {
      id: ESubscriptionRouteName.CONTRACT_NUMBER_REGISTRATION,
      routeName: ESubscriptionRouteName.CONTRACT_NUMBER_REGISTRATION,
      hasStepper: false,
    },
    {
      id: ESubscriptionRouteName.INSTALLATION_ADDRESS,
      routeName: ESubscriptionRouteName.INSTALLATION_ADDRESS,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.EQUIPMENTS,
      routeName: ESubscriptionRouteName.EQUIPMENTS,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.SOLAR_INSTALLATION,
      routeName: ESubscriptionRouteName.SOLAR_INSTALLATION,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.INSTALLER_INFORMATIONS,
      routeName: ESubscriptionRouteName.INSTALLER_INFORMATIONS,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.PHYSICAL_BATTERY,
      routeName: ESubscriptionRouteName.PHYSICAL_BATTERY,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.SOLAR_PRODUCTION_STORAGE,
      routeName: ESubscriptionRouteName.SOLAR_PRODUCTION_STORAGE,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.CASHBACK_ACTIVATION,
      routeName: ESubscriptionRouteName.CASHBACK_ACTIVATION,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.IMPOSSIBLE_SUBSCRIPTION,
      routeName: ESubscriptionRouteName.IMPOSSIBLE_SUBSCRIPTION,
      hasStepper: false,
    },
    {
      id: ESubscriptionRouteName.ELECTRICITY_CONSUMPTION,
      routeName: ESubscriptionRouteName.ELECTRICITY_CONSUMPTION,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.ELECTRICITY_SUPPLIER,
      routeName: ESubscriptionRouteName.ELECTRICITY_SUPPLIER,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.BILLING_MODE,
      routeName: ESubscriptionRouteName.BILLING_MODE,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.CONTRACTOR_INFORMATIONS,
      routeName: ESubscriptionRouteName.CONTRACTOR_INFORMATIONS,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.BILLING_INFORMATIONS,
      routeName: ESubscriptionRouteName.BILLING_INFORMATIONS,
      hasStepper: true,
    },
    {
      id: ESubscriptionRouteName.INFORMATIONS_SUMMARY,
      routeName: ESubscriptionRouteName.INFORMATIONS_SUMMARY,
      hasStepper: false,
    },
    {
      id: ESubscriptionRouteName.CONTRACT_SIGNATURE,
      routeName: ESubscriptionRouteName.CONTRACT_SIGNATURE,
      hasStepper: false,
    },
    {
      id: ESubscriptionRouteName.CONTRACT_SIGNED,
      routeName: ESubscriptionRouteName.CONTRACT_SIGNED,
      hasStepper: false,
    },
  ]);

  const currentStep: Ref<SubscriptionRouteName | null> = ref(null);

  const hasPreviousStep = computed(() => {
    return !!getPreviousStep.value;
  });

  const hasNextStep = computed(() => {
    return !!getNextStep.value;
  });

  const getStepsAvailableForCurrentUser = computed(() => {
    const {
      getIsResubscription,
      isEnergySupplyOnly,
      getHasSolarPanels,
      getHasRobinDevice,
      getHasCompatibleEquipments,
      getHasPhysicalBattery,
    } = useSubscriptionStore();

    const routeRules: Partial<Record<SubscriptionRouteName, () => boolean>> = {
      [ESubscriptionRouteName.CONTRACT_NUMBER_REGISTRATION]: () => getIsResubscription,
      [ESubscriptionRouteName.SOLAR_INSTALLATION]: () => getHasSolarPanels,
      [ESubscriptionRouteName.INSTALLER_INFORMATIONS]: () =>
        getHasRobinDevice && !getHasSolarPanels,
      [ESubscriptionRouteName.PHYSICAL_BATTERY]: () =>
        getIsPhysicalBatteryEnabled() && getHasPhysicalBattery,
      [ESubscriptionRouteName.SOLAR_PRODUCTION_STORAGE]: () => getHasSolarPanels,
      [ESubscriptionRouteName.CASHBACK_ACTIVATION]: () =>
        getHasRobinDevice && getHasCompatibleEquipments,
      [ESubscriptionRouteName.IMPOSSIBLE_SUBSCRIPTION]: () => isEnergySupplyOnly,
      [ESubscriptionRouteName.ELECTRICITY_CONSUMPTION]: () =>
        (getHasSolarPanels || getHasRobinDevice) && !isEnergySupplyOnly,
      [ESubscriptionRouteName.ELECTRICITY_SUPPLIER]: () =>
        (getHasSolarPanels || getHasRobinDevice) && !isEnergySupplyOnly,
      [ESubscriptionRouteName.BILLING_MODE]: () => !isEnergySupplyOnly,
      [ESubscriptionRouteName.CONTRACTOR_INFORMATIONS]: () => !isEnergySupplyOnly,
      [ESubscriptionRouteName.BILLING_INFORMATIONS]: () => !isEnergySupplyOnly,
      [ESubscriptionRouteName.INFORMATIONS_SUMMARY]: () => !isEnergySupplyOnly,
      [ESubscriptionRouteName.CONTRACT_SIGNATURE]: () => !isEnergySupplyOnly,
      [ESubscriptionRouteName.CONTRACT_SIGNED]: () => !isEnergySupplyOnly,
    };
    return allSteps.value.filter(step => {
      const rule = routeRules[step.routeName];
      return rule ? rule() : true;
    });
  });

  const getStepsDisplayOnStepper = computed(() => {
    return getStepsAvailableForCurrentUser.value.filter(step => step.hasStepper);
  });

  const getCurrentStep = computed(() => {
    return allSteps.value.find(step => step.id === currentStep.value);
  });

  const getNextStep = computed(() => {
    if (!currentStep.value) {
      return null;
    }

    const stepIndex = getStepsAvailableForCurrentUser.value.findIndex(
      step => step.id === currentStep.value
    );

    return getStepsAvailableForCurrentUser.value[stepIndex + 1];
  });

  const getPreviousStep = computed(() => {
    if (!currentStep.value) {
      return null;
    }

    const stepIndex = getStepsAvailableForCurrentUser.value.findIndex(
      step => step.id === currentStep.value
    );
    return getStepsAvailableForCurrentUser.value[stepIndex - 1];
  });

  const goToNextStep = async () => {
    const nextStep = getNextStep.value;

    if (nextStep) {
      await router.push({
        name: nextStep.routeName,
      });
    }

    return nextStep;
  };

  const goToPreviousStep = async () => {
    const previousStep = getPreviousStep.value;

    if (previousStep) {
      await router.push({
        name: previousStep.routeName,
      });
    }

    return previousStep;
  };

  const goToStepById = async (stepId: SubscriptionRouteName) => {
    const step = findStepByStepId(stepId);
    if (step) {
      await router.push({
        name: step.routeName,
      });
    }
    return step;
  };

  function setCurrentStep(step: SubscriptionRouteName) {
    currentStep.value = step;
  }

  function findStepByStepId(stepId: SubscriptionRouteName) {
    return getStepsAvailableForCurrentUser.value.find(step => step.id === stepId);
  }

  return {
    getStepsDisplayOnStepper,
    getCurrentStep,
    hasPreviousStep,
    hasNextStep,
    setCurrentStep,
    goToPreviousStep,
    goToNextStep,
    goToStepById,
  };
});
